/**
 * The folder tree.
 *
 * The database stores a flat list with a parent_id; the left pane needs a
 * tree. Building it here rather than in the component keeps the two rules
 * that matter — what nests inside what, and what may be dropped where —
 * testable without rendering anything.
 */

export interface FlatFolder {
  id: string;
  name: string;
  parent_id: string | null;
}

export interface FolderNode extends FlatFolder {
  depth: number;
  children: FolderNode[];
}

/** The root marker used by the pane for "no folder". */
export const ROOT = null;

/**
 * A flat list as a tree, sorted by name at every level.
 *
 * Folders whose parent is missing — deleted underneath us, or belonging to
 * somebody else and therefore invisible — are lifted to the root rather than
 * dropped. A folder that vanishes from the pane because its parent did is a
 * folder somebody cannot reach their notes through.
 */
export function buildTree(folders: FlatFolder[]): FolderNode[] {
  const byId = new Map(folders.map((folder) => [folder.id, folder]));
  const childrenOf = new Map<string | null, FlatFolder[]>();

  for (const folder of folders) {
    const parent =
      folder.parent_id !== null && byId.has(folder.parent_id) ? folder.parent_id : null;
    const siblings = childrenOf.get(parent) ?? [];
    siblings.push(folder);
    childrenOf.set(parent, siblings);
  }

  const seen = new Set<string>();

  function build(parent: string | null, depth: number): FolderNode[] {
    const siblings = [...(childrenOf.get(parent) ?? [])].sort((a, b) =>
      a.name.localeCompare(b.name),
    );
    const out: FolderNode[] = [];
    for (const folder of siblings) {
      // Defence with no test behind it, and deliberately kept. With a single
      // parent_id a cycle forms a component disconnected from the root, so
      // this walk never enters one and no fixture can make it — the folders
      // in the cycle simply do not appear. If parent_id ever becomes a list,
      // or a repair script rewrites parents mid-read, this is what stops a
      // frozen tab.
      if (seen.has(folder.id)) {
        continue;
      }
      seen.add(folder.id);
      out.push({ ...folder, depth, children: build(folder.id, depth + 1) });
    }
    return out;
  }

  return build(null, 0);
}

/** Every folder beneath one, at any depth. */
export function descendantIds(folders: FlatFolder[], id: string): string[] {
  const direct = new Map<string | null, string[]>();
  for (const folder of folders) {
    const siblings = direct.get(folder.parent_id) ?? [];
    siblings.push(folder.id);
    direct.set(folder.parent_id, siblings);
  }
  const out: string[] = [];
  const queue = [...(direct.get(id) ?? [])];
  const seen = new Set<string>();
  while (queue.length > 0) {
    const next = queue.shift();
    if (next === undefined || seen.has(next)) continue;
    seen.add(next);
    out.push(next);
    queue.push(...(direct.get(next) ?? []));
  }
  return out;
}

/**
 * Whether a folder may be dropped onto a target.
 *
 * THE circular-reference guard. Dropping Work onto its own sub-folder Acme
 * makes Work its own ancestor, and every recursive walk over the result runs
 * forever — including the one that draws this pane. The database refuses it
 * too; this is what stops the browser offering the drop in the first place.
 */
export function canDropFolder(
  folders: FlatFolder[],
  dragged: string,
  target: string | null,
): boolean {
  if (dragged === target) {
    return false;
  }
  if (target === null) {
    // Dropping on the root is always legal — it is how a folder is un-nested.
    const current = folders.find((folder) => folder.id === dragged);
    // Except when it is already there, where it is a no-op rather than a move.
    return current !== undefined && current.parent_id !== null;
  }
  if (descendantIds(folders, dragged).includes(target)) {
    return false;
  }
  // Already the direct parent: legal but pointless, and offering it makes a
  // drop that changes nothing look like it failed.
  const current = folders.find((folder) => folder.id === dragged);
  return current?.parent_id !== target;
}

/** Flatten a tree to the rows actually visible, honouring collapsed folders. */
export function visibleRows(tree: FolderNode[], expanded: ReadonlySet<string>): FolderNode[] {
  const out: FolderNode[] = [];
  function walk(nodes: FolderNode[]): void {
    for (const node of nodes) {
      out.push(node);
      if (node.children.length > 0 && expanded.has(node.id)) {
        walk(node.children);
      }
    }
  }
  walk(tree);
  return out;
}

/**
 * The ancestors of a folder, nearest first.
 *
 * Used to open the tree down to a folder somebody has selected — a selection
 * hidden inside a collapsed branch looks like nothing happened.
 */
export function ancestorsOf(folders: FlatFolder[], id: string): string[] {
  const byId = new Map(folders.map((folder) => [folder.id, folder]));
  const out: string[] = [];
  let current = byId.get(id)?.parent_id ?? null;
  let hops = 0;
  while (current !== null && hops < 64) {
    out.push(current);
    current = byId.get(current)?.parent_id ?? null;
    hops += 1;
  }
  return out;
}

/**
 * A folder's full path, e.g. "Work / Drafts".
 *
 * Once folders nest, a bare name stops identifying anything: two "Drafts" in
 * different branches are both legal, and the header pill and the move menu
 * would show the same word for two different places.
 */
export function folderPath(folders: FlatFolder[], id: string, separator = ' / '): string {
  const byId = new Map(folders.map((folder) => [folder.id, folder]));
  const self = byId.get(id);
  if (self === undefined) {
    return '';
  }
  const names = [self.name];
  for (const ancestor of ancestorsOf(folders, id)) {
    const found = byId.get(ancestor);
    if (found !== undefined) {
      names.unshift(found.name);
    }
  }
  return names.join(separator);
}

const STORAGE_KEY = 'tap.notes.expandedFolders';

/**
 * The expanded set, remembered across refreshes.
 *
 * A tree that collapses every time the page reloads is a tree somebody
 * re-opens several times a day. localStorage is the right store: it is per
 * device and per person, and losing it costs nothing.
 */
export function loadExpanded(storage: Pick<Storage, 'getItem'>): Set<string> {
  try {
    const raw = storage.getItem(STORAGE_KEY);
    if (raw === null) return new Set();
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed)
      ? new Set(parsed.filter((id) => typeof id === 'string'))
      : new Set();
  } catch {
    // A corrupt value is not worth a broken pane.
    return new Set();
  }
}

export function saveExpanded(
  storage: Pick<Storage, 'setItem'>,
  expanded: ReadonlySet<string>,
): void {
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify([...expanded]));
  } catch {
    // Private browsing, or a full quota. The tree still works this session.
  }
}
